/**
 * Detalhe do atendimento — o coração do fluxo operacional.
 *
 * A clínica não terá certificação SBIS/CFM: o sistema NÃO é o prontuário legal.
 * O registro oficial é o papel impresso, conferido e assinado de próprio punho
 * pelo médico examinador. Daí a ordem inegociável:
 *
 *     digitar → imprimir → assinar → digitalizar → anexar
 *
 * Nesta fase os médicos não acessam o app: a atendente (perfil administrativo)
 * transcreve ficha, exames e parecer, imprime, colhe a assinatura e anexa a
 * digitalização. Por isso não há mais bloqueio por perfil nos atos de digitação.
 *
 * Quem trava a tela é o DOCUMENTO, não o perfil: enquanto existir ASO emitido
 * não-cancelado, ficha/exames/parecer ficam somente-leitura, pra a cópia digital
 * não divergir do papel assinado. Para corrigir, cancela-se o ASO (com motivo),
 * o atendimento destrava e a nova emissão recebe número novo.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { apiFetchBlob } from "@/auth/api";
import {
  useAdicionarExameRealizado,
  useExamesParaSelecao,
  useCriarExame,
  useMedicos,
  useTrocarMedicoAtendimento,
  useExames,
  useAnexos,
  useAtendimento,
  useCancelarAso,
  useCancelarAtendimento,
  useEmitirParecer,
  useEnviarAnexo,
  useGerarAso,
  useMarcarAsoAssinado,
  useMarcarAsoImpresso,
  useRemoverAnexo,
  useRemoverExameRealizado,
  useSalvarFicha,
} from "@/hooks/useApi";
import { useHasRole } from "@/hooks/useUser";
import type { ResultadoParecer, TipoAnexo } from "@/types/api";
import {
  RESULTADO_PARECER_ROTULOS,
  STATUS_ASO_BADGE,
  TIPO_ANEXO_ROTULOS,
  TIPO_EXAME_ROTULOS,
} from "@/types/api";
import { rotuloEscopo } from "@/types/extracao";
import { STATUS_ATENDIMENTO_BADGE } from "@/pages/Atendimentos";
import { fmtData, SITUACAO_BADGE } from "@/pages/Pendencias";

function hoje(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${dia}`;
}

function fmtDataHora(iso: string | null): string {
  if (!iso) return "—";
  const [data, resto] = iso.split("T");
  const [a, m, d] = data.split("-");
  const hora = (resto ?? "").slice(0, 5);
  return hora ? `${d}/${m}/${a} ${hora}` : `${d}/${m}/${a}`;
}

function fmtTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

const AVISO_SOMENTE_LEITURA = "somente-leitura — ASO emitido";

const TIPOS_ANEXO: TipoAnexo[] = ["ficha_assinada", "aso_assinado", "resultado_exame", "outro"];

export function AtendimentoDetalhePage() {
  const { id } = useParams();
  const atendimentoId = id ? Number(id) : null;
  const location = useLocation();
  const alertaDispensa = (location.state as { alerta_dispensa?: string } | null)?.alerta_dispensa;

  const isAdmin = useHasRole("administrativo");
  const { data: atend, isLoading, error } = useAtendimento(atendimentoId);
  const { data: anexos } = useAnexos(atendimentoId);

  const salvarFicha = useSalvarFicha(atendimentoId);
  const addExame = useAdicionarExameRealizado(atendimentoId);
  const delExame = useRemoverExameRealizado(atendimentoId);
  const emitirParecer = useEmitirParecer(atendimentoId);
  const gerarAso = useGerarAso(atendimentoId);
  const marcarImpresso = useMarcarAsoImpresso();
  const marcarAssinado = useMarcarAsoAssinado();
  const cancelarAso = useCancelarAso();
  const cancelar = useCancelarAtendimento();
  const enviarAnexo = useEnviarAnexo(atendimentoId);
  const removerAnexo = useRemoverAnexo(atendimentoId);

  // ---- Ficha clínica (estado local, carregado uma vez) ----
  const [anamnese, setAnamnese] = useState("");
  const [exameFisico, setExameFisico] = useState("");
  const [antecedentes, setAntecedentes] = useState("");
  const [fichaCarregada, setFichaCarregada] = useState(false);
  useEffect(() => {
    if (atend && !fichaCarregada) {
      setAnamnese(atend.ficha?.anamnese ?? "");
      setExameFisico(atend.ficha?.exame_fisico ?? "");
      setAntecedentes(atend.ficha?.antecedentes ?? "");
      setFichaCarregada(true);
    }
  }, [atend, fichaCarregada]);

  // ---- Novo exame realizado ----
  const [novoExameId, setNovoExameId] = useState<number | null>(null);

  // ---- Exame solicitado pela empresa (fora da bateria do PCMSO) ----
  // Busca no catálogo inteiro, não só nas exigências do GHE, e permite
  // cadastrar o que não existir — a empresa pede o que quiser.
  const criarExame = useCriarExame();
  const { data: catalogoTodo } = useExamesParaSelecao();

  // ---- Troca do médico examinador (escolhido errado na abertura) ----
  const trocarMedico = useTrocarMedicoAtendimento(atendimentoId);
  const { data: medicos } = useMedicos();
  const [trocandoMedico, setTrocandoMedico] = useState(false);
  // Cancelar exige justificativa: o atendimento cancelado continua no histórico
  // do colaborador e sem o motivo ninguém entende depois por que ele morreu.
  const [cancelandoAtend, setCancelandoAtend] = useState(false);
  const [motivoCancelarAtend, setMotivoCancelarAtend] = useState("");
  const [medicoNovoId, setMedicoNovoId] = useState<number | null>(null);
  const [buscaEmpresa, setBuscaEmpresa] = useState("");
  const [exameEmpresaId, setExameEmpresaId] = useState<number | null>(null);
  const [dataEmpresa, setDataEmpresa] = useState(hoje());
  const { data: catalogo, isFetching: buscandoExame } = useExames(buscaEmpresa);
  const [novaData, setNovaData] = useState(hoje());
  const [novoResultado, setNovoResultado] = useState("");

  // ---- Parecer ----
  const [resultado, setResultado] = useState<ResultadoParecer>("apto");
  const [restricoes, setRestricoes] = useState("");
  const [aptidoes, setAptidoes] = useState<Record<number, boolean>>({});
  useEffect(() => {
    if (atend?.ghe && !atend.parecer) {
      setAptidoes((prev) => {
        const next = { ...prev };
        for (const a of atend.ghe!.aptidoes) {
          if (!(a.id in next)) next[a.id] = true;
        }
        return next;
      });
    }
  }, [atend]);

  // ---- Documentos digitalizados ----
  const [tipoAnexo, setTipoAnexo] = useState<TipoAnexo>("ficha_assinada");
  const [descricaoAnexo, setDescricaoAnexo] = useState("");
  const [arquivoAnexo, setArquivoAnexo] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // ---- Cancelamento do ASO (formulário inline; motivo obrigatório) ----
  const [formCancelamento, setFormCancelamento] = useState(false);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");

  const [erroPdf, setErroPdf] = useState<string | null>(null);
  const [erroAnexo, setErroAnexo] = useState<string | null>(null);
  const [gerandoManual, setGerandoManual] = useState(false);

  if (isLoading) return <p className="text-ink-muted">Carregando…</p>;
  if (error) return <p className="text-sm text-anarosa">{(error as Error).message}</p>;
  if (!atend) return <p className="text-sm text-ink-muted">Atendimento não encontrado.</p>;

  const emAndamento = atend.status === "em_andamento";
  // Travamento é do documento: ASO emitido não-cancelado congela a digitação.
  // Atendimento 'concluido' (com parecer) segue editável enquanto não há ASO —
  // é a janela pra corrigir a transcrição antes de emitir.
  const asoAtivo = atend.aso && atend.aso.status !== "cancelado" ? atend.aso : null;
  const asoCancelado = atend.aso && atend.aso.status === "cancelado" ? atend.aso : null;
  const travado = asoAtivo !== null;
  const editavel = atend.status !== "cancelado" && !travado;
  // Duas listas a partir da mesma fonte: a bateria do PCMSO e o que a empresa
  // pediu por fora. O ASO imprime cada uma na sua seção.
  const examesPcmso = atend.exames_realizados.filter((er) => !er.solicitado_pela_empresa);
  const examesEmpresa = atend.exames_realizados.filter((er) => er.solicitado_pela_empresa);
  const temAsoAssinadoAnexado = (anexos ?? []).some((a) => a.tipo === "aso_assinado");

  // O motivo do cancelamento só volta na resposta da própria mutação — o detalhe
  // do atendimento ainda não expõe o campo.
  const motivoRegistrado =
    (cancelarAso.data && asoCancelado && cancelarAso.data.id === asoCancelado.id
      ? cancelarAso.data.cancelado_motivo
      : asoCancelado?.cancelado_motivo) ?? null;

  const notaTranscricao =
    `Transcrição do registro do Dr(a). ${atend.medico.nome} — ` +
    `CRM ${atend.medico.crm}/${atend.medico.uf_crm}. A responsabilidade clínica é do ` +
    `médico examinador, que confere e assina o documento impresso.`;

  async function abrirPdf(asoId: number) {
    setErroPdf(null);
    try {
      const { blob } = await apiFetchBlob(`/asos/${asoId}/pdf`);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setErroPdf((e as Error).message);
    }
  }

  /** ASO em branco (sem número, sem parecer marcado) pro médico preencher à mão. */
  async function imprimirParaPreenchimento() {
    if (atendimentoId === null) return;
    setErroPdf(null);
    setGerandoManual(true);
    try {
      const { blob } = await apiFetchBlob(`/atendimentos/${atendimentoId}/aso-para-preenchimento`);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setErroPdf((e as Error).message);
    } finally {
      setGerandoManual(false);
    }
  }

  async function abrirAnexo(anexoId: number) {
    setErroAnexo(null);
    try {
      const { blob } = await apiFetchBlob(`/anexos/${anexoId}/arquivo`);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setErroAnexo((e as Error).message);
    }
  }

  async function handleEnviarAnexo() {
    if (!arquivoAnexo) return;
    setErroAnexo(null);
    try {
      await enviarAnexo.mutateAsync({
        tipo: tipoAnexo,
        descricao: descricaoAnexo || undefined,
        arquivo: arquivoAnexo,
      });
      setArquivoAnexo(null);
      setDescricaoAnexo("");
      if (fileRef.current) fileRef.current.value = "";
    } catch {
      /* erro via enviarAnexo.error */
    }
  }

  async function handleRemoverAnexo(anexoId: number, nome: string) {
    if (!window.confirm(`Remover o documento "${nome}"?`)) return;
    try {
      await removerAnexo.mutateAsync(anexoId);
    } catch {
      /* erro (409 quando travado) via removerAnexo.error */
    }
  }

  /** Adiciona exame pedido pela empresa; cadastra no catálogo se for nome novo. */
  async function handleAdicionarExameEmpresa() {
    const termo = buscaEmpresa.trim();
    if (exameEmpresaId === null && termo.length < 2) return;
    try {
      const id = exameEmpresaId ?? (await criarExame.mutateAsync(termo)).id;
      await addExame.mutateAsync({
        exame_id: id,
        data_realizacao: dataEmpresa,
        solicitado_pela_empresa: true,
      });
      setBuscaEmpresa("");
      setExameEmpresaId(null);
    } catch {
      /* erro via addExame.error / criarExame.error */
    }
  }

  async function handleAdicionarExame() {
    if (novoExameId === null) return;
    try {
      await addExame.mutateAsync({
        exame_id: novoExameId,
        data_realizacao: novaData,
        resultado: novoResultado || null,
      });
      setNovoExameId(null);
      setNovoResultado("");
    } catch {
      /* erro via addExame.error */
    }
  }

  async function handleEmitirParecer() {
    const rotulo = RESULTADO_PARECER_ROTULOS[resultado];
    if (!window.confirm(
      `Emitir parecer "${rotulo}"?\n\nO atendimento será CONCLUÍDO. A digitação segue corrigível até a emissão do ASO.`
    )) return;
    try {
      await emitirParecer.mutateAsync({
        resultado,
        restricoes: resultado === "apto_com_restricao" ? restricoes || null : null,
        aptidoes: (atend?.ghe?.aptidoes ?? []).map((a) => ({
          aptidao_id: a.id,
          apto: aptidoes[a.id] ?? true,
        })),
      });
    } catch {
      /* erro via emitirParecer.error */
    }
  }

  async function handleCancelarAso(asoId: number) {
    if (motivoCancelamento.trim().length < 3) return;
    try {
      await cancelarAso.mutateAsync({ asoId, motivo: motivoCancelamento.trim() });
      setFormCancelamento(false);
      setMotivoCancelamento("");
    } catch {
      /* erro via cancelarAso.error */
    }
  }

  async function handleCancelar() {
    if (atendimentoId === null || motivoCancelarAtend.trim().length < 5) return;
    if (!window.confirm("Cancelar este atendimento? A ação não pode ser desfeita.")) return;
    try {
      await cancelar.mutateAsync({ atendimentoId, motivo: motivoCancelarAtend.trim() });
      setCancelandoAtend(false);
    } catch {
      /* erro via cancelar.error */
    }
  }

  return (
    <div className="space-y-6">
      {/* ---------- Cabeçalho ---------- */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">
            Atendimento #{atend.id} — {atend.colaborador.nome}
          </h2>
          <p className="text-sm text-ink-muted">
            {atend.colaborador.empresa} · {atend.colaborador.funcao} ({atend.colaborador.setor})
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={STATUS_ATENDIMENTO_BADGE[atend.status].classe}>
            {STATUS_ATENDIMENTO_BADGE[atend.status].rotulo}
          </span>
          <Link className="btn-secondary text-xs" to="/atendimentos">← Atendimentos</Link>
        </div>
      </div>

      {alertaDispensa ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          ⚠ {alertaDispensa}
        </div>
      ) : null}

      {/* ---------- Travamento pós-emissão ---------- */}
      {asoAtivo ? (
        <div className="rounded-md border border-amber-300 border-l-4 border-l-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-semibold">
            Documento já emitido ({asoAtivo.numero}) — ficha, exames e parecer estão
            somente-leitura. Para corrigir, cancele o ASO.
          </p>
          <p className="mt-1 text-xs">
            O papel impresso e assinado é o registro oficial: a cópia digital não pode divergir
            dele. Anexar digitalizações continua liberado.
          </p>
        </div>
      ) : null}

      <div className="card grid grid-cols-2 gap-3 p-4 text-sm md:grid-cols-4">
        <div><span className="block text-xs text-ink-muted">Tipo</span>{TIPO_EXAME_ROTULOS[atend.tipo]}</div>
        <div><span className="block text-xs text-ink-muted">Data</span>{fmtData(atend.data_atendimento)}</div>
        <div>
          <span className="block text-xs text-ink-muted">GHE aplicado</span>
          {atend.ghe ? `${atend.ghe.codigo}${atend.ghe.nome ? ` — ${atend.ghe.nome}` : ""}` : "—"}
          {atend.documento_pcmso_id !== null ? (
            <span className="block text-xs text-ink-subtle">
              PCMSO #{atend.documento_pcmso_id} · escopo: {rotuloEscopo(atend.pcmso_escopo)}
            </span>
          ) : null}
        </div>
        <div>
          <span className="block text-xs text-ink-muted">Médico examinador</span>
          {atend.medico.nome} (CRM {atend.medico.crm}/{atend.medico.uf_crm})
          {editavel && !atend.parecer && !trocandoMedico ? (
            <button className="ml-2 text-xs text-anarosa hover:underline"
              onClick={() => { setMedicoNovoId(null); setTrocandoMedico(true); }}>
              trocar
            </button>
          ) : null}
          {trocandoMedico ? (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <select className="input w-64 py-1 text-sm" value={medicoNovoId ?? ""}
                onChange={(e) => setMedicoNovoId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">selecione o médico…</option>
                {(medicos ?? [])
                  .filter((m) => m.id !== atend.medico.id)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.nome} — CRM {m.crm}/{m.uf_crm}
                    </option>
                  ))}
              </select>
              <button className="btn-secondary text-xs"
                disabled={medicoNovoId === null || trocarMedico.isPending}
                onClick={async () => {
                  try {
                    await trocarMedico.mutateAsync(medicoNovoId!);
                    setTrocandoMedico(false);
                  } catch { /* erro abaixo */ }
                }}>
                {trocarMedico.isPending ? "Trocando…" : "Confirmar"}
              </button>
              <button className="text-xs text-ink-muted hover:underline"
                onClick={() => setTrocandoMedico(false)}>cancelar</button>
            </div>
          ) : null}
          {trocarMedico.error ? (
            <p className="mt-1 text-xs text-anarosa">{(trocarMedico.error as Error).message}</p>
          ) : null}
        </div>
        <div><span className="block text-xs text-ink-muted">CPF</span>{atend.colaborador.cpf}</div>
        <div><span className="block text-xs text-ink-muted">Unidade</span>{atend.colaborador.unidade}</div>
        <div className="col-span-2">
          <span className="block text-xs text-ink-muted">Riscos do GHE</span>
          {atend.ghe && atend.ghe.riscos.length > 0
            ? atend.ghe.riscos.map((r) => r.perigo).join("; ")
            : "Ausência de riscos ocupacionais específicos"}
        </div>
      </div>

      {atend.status === "cancelado" && atend.cancelado_motivo ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <strong>Atendimento cancelado.</strong> Motivo: {atend.cancelado_motivo}
          {atend.cancelado_por ? ` — por ${atend.cancelado_por}` : ""}
        </div>
      ) : null}

      {/* Cancelar vale inclusive COM parecer: é o caminho de correção quando o
          parecer saiu em nome do médico errado (o parecer não pode ser
          refeito). O que trava é o ASO emitido — ali cancela-se o ASO antes. */}
      {isAdmin && atend.status !== "cancelado" && !asoAtivo ? (
        <div className="space-y-2">
          {cancelandoAtend ? (
            <div className="card space-y-2 p-3">
              <label className="block text-xs font-medium text-ink-muted">
                Por que este atendimento está sendo cancelado?
              </label>
              <input className="input" autoFocus value={motivoCancelarAtend}
                placeholder="ex.: médico examinador errado no parecer"
                onChange={(e) => setMotivoCancelarAtend(e.target.value)} />
              <p className="text-xs text-ink-muted">
                O atendimento continua no histórico do colaborador, com este motivo.
                Para refazer, abra um novo atendimento.
              </p>
              <div className="flex items-center gap-2">
                <button className="btn-secondary text-xs text-anarosa"
                  disabled={cancelar.isPending || motivoCancelarAtend.trim().length < 5}
                  onClick={handleCancelar}>
                  {cancelar.isPending ? "Cancelando…" : "Confirmar cancelamento"}
                </button>
                <button className="text-xs text-ink-muted hover:underline"
                  onClick={() => setCancelandoAtend(false)}>voltar</button>
              </div>
            </div>
          ) : (
            <button className="btn-secondary text-xs text-anarosa"
              onClick={() => { setMotivoCancelarAtend(""); setCancelandoAtend(true); }}>
              Cancelar atendimento
            </button>
          )}
          {cancelar.error ? (
            <p className="text-sm text-anarosa">{(cancelar.error as Error).message}</p>
          ) : null}
        </div>
      ) : null}

      {/* ---------- Exigências do PCMSO ---------- */}
      <div className="card p-4">
        <h3 className="font-display text-base font-semibold">Exigências do PCMSO</h3>
        <p className="text-xs text-ink-muted">
          Exames exigidos pro GHE {atend.ghe?.codigo ?? "—"} com a situação atual do colaborador.
        </p>
        {atend.exigencias.length === 0 ? (
          <p className="mt-2 text-sm text-ink-muted">Nenhuma exigência cadastrada pro GHE.</p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-2 py-1">Exame</th>
                <th className="px-2 py-1">Periodicidade</th>
                <th className="px-2 py-1">Última realização</th>
                <th className="px-2 py-1">Vence em</th>
                <th className="px-2 py-1">Situação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {atend.exigencias.map((ex) => (
                <tr key={ex.exame_id}>
                  <td className="px-2 py-1.5 font-medium">{ex.exame}</td>
                  <td className="px-2 py-1.5 text-ink-muted">
                    {ex.periodicidade_meses ? `${ex.periodicidade_meses} meses` : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-ink-muted">{fmtData(ex.ultima_realizacao)}</td>
                  <td className="px-2 py-1.5 text-ink-muted">{fmtData(ex.vence_em)}</td>
                  <td className="px-2 py-1.5">
                    {ex.situacao ? (
                      <span className={SITUACAO_BADGE[ex.situacao].classe}>
                        {SITUACAO_BADGE[ex.situacao].rotulo}
                      </span>
                    ) : (
                      <span className="badge bg-gray-100 text-ink-muted">n/a</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ---------- Ficha Clínica ---------- */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-semibold">Ficha Clínica</h3>
          {travado ? (
            <span className="badge bg-amber-100 text-amber-800">{AVISO_SOMENTE_LEITURA}</span>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-ink-muted">{notaTranscricao}</p>
        <fieldset disabled={!editavel} className="mt-3 grid gap-3 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">Anamnese</label>
            <textarea className="input min-h-28" value={anamnese}
              onChange={(e) => setAnamnese(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">Exame físico</label>
            <textarea className="input min-h-28" value={exameFisico}
              onChange={(e) => setExameFisico(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">Antecedentes</label>
            <textarea className="input min-h-28" value={antecedentes}
              onChange={(e) => setAntecedentes(e.target.value)} />
          </div>
        </fieldset>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button className="btn-primary" disabled={!editavel || salvarFicha.isPending}
            onClick={() => salvarFicha.mutate({
              anamnese: anamnese || null,
              exame_fisico: exameFisico || null,
              antecedentes: antecedentes || null,
            })}>
            {salvarFicha.isPending ? "Salvando…" : "Salvar ficha"}
          </button>
          {atend.ficha?.preenchido_em ? (
            <span className="text-xs text-ink-muted">
              Última gravação: {fmtData(atend.ficha.preenchido_em)}
            </span>
          ) : null}
          {salvarFicha.error ? (
            <p className="text-sm text-anarosa">{(salvarFicha.error as Error).message}</p>
          ) : null}
        </div>
      </div>

      {/* ---------- Exames Realizados ---------- */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-semibold">Exames Realizados</h3>
          {travado ? (
            <span className="badge bg-amber-100 text-amber-800">{AVISO_SOMENTE_LEITURA}</span>
          ) : null}
        </div>
        {examesPcmso.length === 0 ? (
          <p className="mt-2 text-sm text-ink-muted">Nenhum exame registrado neste atendimento.</p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-2 py-1">Data</th>
                <th className="px-2 py-1">Exame</th>
                <th className="px-2 py-1">Resultado</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {examesPcmso.map((er) => (
                <tr key={er.id}>
                  <td className="px-2 py-1.5 text-ink-muted">{fmtData(er.data_realizacao)}</td>
                  <td className="px-2 py-1.5 font-medium">{er.exame}</td>
                  <td className="px-2 py-1.5 text-ink-muted">{er.resultado ?? "—"}</td>
                  <td className="px-2 py-1.5 text-right">
                    {editavel ? (
                      <button className="text-xs text-anarosa hover:underline"
                        disabled={delExame.isPending}
                        onClick={() => delExame.mutate(er.id)}>
                        remover
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <fieldset disabled={!editavel}
          className="mt-3 flex flex-wrap items-end gap-3 border-t border-gray-100 pt-3">
          <div className="w-64">
            <label className="mb-1 block text-xs font-medium text-ink-muted">Exame</label>
            {/* A bateria do GHE vem primeiro, mas o catálogo inteiro fica
                disponível abaixo: nem sempre o que foi feito está na bateria
                (ex.: sai só o exame clínico, sem os demais). */}
            <select className="input" value={novoExameId ?? ""}
              onChange={(e) => setNovoExameId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">selecione…</option>
              <optgroup label="Exigências do GHE">
                {atend.exigencias.map((ex) => (
                  <option key={ex.exame_id} value={ex.exame_id}>{ex.exame}</option>
                ))}
              </optgroup>
              <optgroup label="Outros exames do catálogo">
                {(catalogoTodo ?? [])
                  .filter((x) => !atend.exigencias.some((ex) => ex.exame_id === x.id))
                  .map((x) => (
                    <option key={x.id} value={x.id}>{x.nome}</option>
                  ))}
              </optgroup>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">Data</label>
            <input type="date" className="input w-40" value={novaData}
              onChange={(e) => setNovaData(e.target.value)} />
          </div>
          <div className="w-56">
            <label className="mb-1 block text-xs font-medium text-ink-muted">Resultado (opcional)</label>
            <input className="input" value={novoResultado}
              onChange={(e) => setNovoResultado(e.target.value)} />
          </div>
          <button className="btn-secondary" disabled={novoExameId === null || addExame.isPending}
            onClick={handleAdicionarExame}>
            {addExame.isPending ? "Adicionando…" : "+ Adicionar"}
          </button>
        </fieldset>
        {addExame.error ? (
          <p className="mt-2 text-sm text-anarosa">{(addExame.error as Error).message}</p>
        ) : null}
        {delExame.error ? (
          <p className="mt-2 text-sm text-anarosa">{(delExame.error as Error).message}</p>
        ) : null}
      </div>

      {/* ---------- Exames solicitados pela empresa ---------- */}
      {/* Fora da bateria do PCMSO: a empresa cliente pede por conta própria.
          Saem em seção separada do ASO e NÃO contam como exigência do
          programa, então não viram pendência do colaborador. */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-semibold">Exames solicitados pela empresa</h3>
          {travado ? (
            <span className="badge bg-amber-100 text-amber-800">{AVISO_SOMENTE_LEITURA}</span>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-ink-muted">
          Exames pedidos pela empresa, fora da bateria do PCMSO. Saem em seção
          própria do ASO e não geram pendência.
        </p>
        {examesEmpresa.length === 0 ? (
          <p className="mt-2 text-sm text-ink-muted">Nenhum exame solicitado pela empresa.</p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-2 py-1">Data</th>
                <th className="px-2 py-1">Exame</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {examesEmpresa.map((er) => (
                <tr key={er.id}>
                  <td className="px-2 py-1.5 text-ink-muted">{fmtData(er.data_realizacao)}</td>
                  <td className="px-2 py-1.5 font-medium">{er.exame}</td>
                  <td className="px-2 py-1.5 text-right">
                    {editavel ? (
                      <button className="text-xs text-anarosa hover:underline"
                        disabled={delExame.isPending}
                        onClick={() => delExame.mutate(er.id)}>
                        remover
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <fieldset disabled={!editavel}
          className="mt-3 flex flex-wrap items-end gap-3 border-t border-gray-100 pt-3">
          <div className="w-72">
            <label className="mb-1 block text-xs font-medium text-ink-muted">
              Exame (busque ou digite um novo)
            </label>
            <input className="input" list="catalogo-exames" value={buscaEmpresa}
              placeholder="ex.: Toxicológico CLT"
              onChange={(e) => {
                setBuscaEmpresa(e.target.value);
                const achado = (catalogo ?? []).find((x) => x.nome === e.target.value);
                setExameEmpresaId(achado ? achado.id : null);
              }} />
            <datalist id="catalogo-exames">
              {(catalogo ?? []).map((x) => <option key={x.id} value={x.nome} />)}
            </datalist>
            {buscaEmpresa.trim().length >= 2 && exameEmpresaId === null ? (
              <p className="mt-0.5 text-xs text-ink-muted">
                {buscandoExame ? "buscando…" : "não está no catálogo — será cadastrado"}
              </p>
            ) : null}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">Data</label>
            <input type="date" className="input w-40" value={dataEmpresa}
              onChange={(e) => setDataEmpresa(e.target.value)} />
          </div>
          <button className="btn-secondary"
            disabled={buscaEmpresa.trim().length < 2 || addExame.isPending || criarExame.isPending}
            onClick={handleAdicionarExameEmpresa}>
            {addExame.isPending || criarExame.isPending ? "Adicionando…" : "+ Adicionar"}
          </button>
        </fieldset>
        {/* Os DOIS erros precisam aparecer aqui: cadastrar o exame no catálogo e
            gravá-lo no atendimento são chamadas distintas. Antes só o primeiro
            era mostrado, então uma falha na gravação (ex.: atendimento travado
            por ASO já emitido) passava em silêncio e o exame parecia salvo. */}
        {criarExame.error ? (
          <p className="mt-2 text-sm text-anarosa">{(criarExame.error as Error).message}</p>
        ) : null}
        {addExame.error ? (
          <p className="mt-2 text-sm text-anarosa">{(addExame.error as Error).message}</p>
        ) : null}
      </div>

      {/* ---------- Parecer ---------- */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-semibold">Parecer</h3>
          {travado ? (
            <span className="badge bg-amber-100 text-amber-800">{AVISO_SOMENTE_LEITURA}</span>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-ink-muted">{notaTranscricao}</p>

        {atend.parecer ? (
          <div className="mt-3 space-y-2 text-sm">
            <p>
              <span className={
                atend.parecer.resultado === "inapto"
                  ? "badge bg-red-100 text-red-800"
                  : "badge bg-emerald-100 text-emerald-800"
              }>
                {RESULTADO_PARECER_ROTULOS[atend.parecer.resultado]}
              </span>
              <span className="ml-2 text-ink-muted">
                emitido por {atend.parecer.emitido_por} em {fmtData(atend.parecer.emitido_em)}
              </span>
            </p>
            {atend.parecer.restricoes ? (
              <p><span className="font-medium">Restrições:</span> {atend.parecer.restricoes}</p>
            ) : null}
            {atend.parecer.aptidoes.length > 0 ? (
              <ul className="space-y-1">
                {atend.parecer.aptidoes.map((a) => (
                  <li key={a.aptidao_id}>
                    <span className={a.apto
                      ? "badge bg-emerald-100 text-emerald-800"
                      : "badge bg-red-100 text-red-800"}>
                      {a.apto ? "Apto" : "Inapto"}
                    </span>
                    <span className="ml-2">{a.nome}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : emAndamento ? (
          <fieldset disabled={!editavel} className="mt-3 space-y-4">
            <div className="flex flex-wrap gap-4">
              {(["apto", "apto_com_restricao", "inapto"] as ResultadoParecer[]).map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="resultado" className="h-4 w-4 accent-anarosa"
                    checked={resultado === r} onChange={() => setResultado(r)} />
                  {RESULTADO_PARECER_ROTULOS[r]}
                </label>
              ))}
            </div>
            {resultado === "apto_com_restricao" ? (
              <div>
                <label className="mb-1 block text-xs font-medium text-ink-muted">Restrições</label>
                <textarea className="input min-h-20" value={restricoes}
                  onChange={(e) => setRestricoes(e.target.value)} />
              </div>
            ) : null}
            {(atend.ghe?.aptidoes ?? []).length > 0 ? (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-muted">
                  Aptidões específicas do GHE
                </p>
                <div className="space-y-2">
                  {atend.ghe!.aptidoes.map((a) => {
                    const apto = aptidoes[a.id] ?? true;
                    return (
                      <div key={a.id} className="flex items-center gap-3 text-sm">
                        <span className="w-64">{a.nome}</span>
                        <button type="button"
                          className={`badge border ${apto
                            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                            : "border-gray-200 bg-white text-ink-subtle"}`}
                          onClick={() => setAptidoes((p) => ({ ...p, [a.id]: true }))}>
                          Apto
                        </button>
                        <button type="button"
                          className={`badge border ${!apto
                            ? "border-red-300 bg-red-50 text-red-800"
                            : "border-gray-200 bg-white text-ink-subtle"}`}
                          onClick={() => setAptidoes((p) => ({ ...p, [a.id]: false }))}>
                          Inapto
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <div className="flex items-center gap-3">
              <button className="btn-primary" disabled={!editavel || emitirParecer.isPending}
                onClick={handleEmitirParecer}>
                {emitirParecer.isPending ? "Emitindo…" : "Emitir parecer"}
              </button>
              <span className="text-xs text-ink-muted">Conclui o atendimento.</span>
              {emitirParecer.error ? (
                <p className="text-sm text-anarosa">{(emitirParecer.error as Error).message}</p>
              ) : null}
            </div>
          </fieldset>
        ) : (
          <p className="mt-2 text-sm text-ink-muted">Atendimento {atend.status} sem parecer.</p>
        )}
      </div>

      {/* ---------- Documentos digitalizados ---------- */}
      <div className="card p-4">
        <h3 className="font-display text-base font-semibold">Documentos digitalizados</h3>
        <p className="mt-1 text-xs text-ink-muted">
          É aqui que entram a <strong>ficha clínica assinada</strong> e o{" "}
          <strong>ASO assinado</strong> depois de impressos, assinados à mão pelo médico
          examinador e escaneados — além de resultados de exames em papel. O anexo do tipo
          “ASO assinado” é pré-requisito pra marcar o ASO como assinado. PDF, JPG ou PNG,
          até 30 MB.
        </p>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="w-56">
            <label className="mb-1 block text-xs font-medium text-ink-muted">Tipo</label>
            <select className="input" value={tipoAnexo}
              onChange={(e) => setTipoAnexo(e.target.value as TipoAnexo)}>
              {TIPOS_ANEXO.map((t) => (
                <option key={t} value={t}>{TIPO_ANEXO_ROTULOS[t]}</option>
              ))}
            </select>
          </div>
          <div className="min-w-64 flex-1">
            <label className="mb-1 block text-xs font-medium text-ink-muted">
              Descrição (opcional)
            </label>
            <input className="input" value={descricaoAnexo}
              onChange={(e) => setDescricaoAnexo(e.target.value)}
              placeholder="Ex.: audiometria realizada na clínica parceira" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">
              Arquivo (PDF, JPG ou PNG)
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="text-sm text-ink-muted file:mr-3 file:rounded-md file:border-0 file:bg-anarosa-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-anarosa-700 hover:file:bg-anarosa-100"
              onChange={(e) => setArquivoAnexo(e.target.files?.[0] ?? null)}
            />
          </div>
          <button className="btn-primary" disabled={!arquivoAnexo || enviarAnexo.isPending}
            onClick={handleEnviarAnexo}>
            {enviarAnexo.isPending ? "Enviando…" : "Anexar documento"}
          </button>
        </div>
        {enviarAnexo.error ? (
          <p className="mt-2 text-sm text-anarosa">{(enviarAnexo.error as Error).message}</p>
        ) : null}

        {(anexos ?? []).length === 0 ? (
          <p className="mt-3 text-sm text-ink-muted">
            Nenhum documento digitalizado anexado a este atendimento.
          </p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-2 py-1">Tipo</th>
                <th className="px-2 py-1">Arquivo</th>
                <th className="px-2 py-1">Tamanho</th>
                <th className="px-2 py-1">Enviado por</th>
                <th className="px-2 py-1">Enviado em</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(anexos ?? []).map((an) => (
                <tr key={an.id}>
                  <td className="px-2 py-1.5 font-medium">{TIPO_ANEXO_ROTULOS[an.tipo]}</td>
                  <td className="px-2 py-1.5">
                    {an.nome_arquivo}
                    {an.descricao ? (
                      <span className="block text-xs text-ink-subtle">{an.descricao}</span>
                    ) : null}
                  </td>
                  <td className="px-2 py-1.5 text-ink-muted">{fmtTamanho(an.tamanho_bytes)}</td>
                  <td className="px-2 py-1.5 text-ink-muted">{an.enviado_por}</td>
                  <td className="px-2 py-1.5 text-ink-muted">{fmtDataHora(an.enviado_em)}</td>
                  <td className="px-2 py-1.5 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button className="text-xs text-anarosa hover:underline"
                        onClick={() => abrirAnexo(an.id)}>
                        Abrir
                      </button>
                      <button className="text-xs text-anarosa hover:underline"
                        disabled={removerAnexo.isPending}
                        onClick={() => handleRemoverAnexo(an.id, an.nome_arquivo)}>
                        Remover
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {removerAnexo.error ? (
          <p className="mt-2 text-sm text-anarosa">{(removerAnexo.error as Error).message}</p>
        ) : null}
        {erroAnexo ? <p className="mt-2 text-sm text-anarosa">{erroAnexo}</p> : null}
      </div>

      {/* ---------- ASO ---------- */}
      <div className="card p-4">
        <h3 className="font-display text-base font-semibold">ASO</h3>
        <p className="mt-1 text-xs text-ink-muted">
          Ciclo do documento: digitar → imprimir → assinar à mão → digitalizar → anexar →
          marcar como assinado.
        </p>

        {/* Impressão pra preenchimento à mão — não depende de ficha nem de parecer */}
        <div className="mt-3 flex flex-wrap items-center gap-3 border-b border-gray-100 pb-3">
          <button className="btn-secondary" disabled={gerandoManual}
            onClick={imprimirParaPreenchimento}>
            {gerandoManual ? "Gerando…" : "Imprimir ASO para preenchimento manual"}
          </button>
          <span className="text-xs text-ink-muted">
            Imprima, entregue ao médico examinador para preencher e assinar à mão.
          </span>
        </div>

        {asoAtivo ? (
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="font-mono font-semibold">{asoAtivo.numero}</span>
              <span className={STATUS_ASO_BADGE[asoAtivo.status].classe}>
                {STATUS_ASO_BADGE[asoAtivo.status].rotulo}
              </span>
              <span className="text-ink-muted">gerado em {fmtData(asoAtivo.gerado_em)}</span>
              <button className="btn-secondary px-2 py-1 text-xs" onClick={() => abrirPdf(asoAtivo.id)}>
                Abrir PDF
              </button>
              {asoAtivo.status === "gerado" ? (
                <button className="btn-secondary px-2 py-1 text-xs"
                  disabled={marcarImpresso.isPending}
                  onClick={() => marcarImpresso.mutate(asoAtivo.id)}>
                  {marcarImpresso.isPending ? "Marcando…" : "Marcar como impresso"}
                </button>
              ) : null}
              {asoAtivo.status !== "assinado" ? (
                <button className="btn-primary px-2 py-1 text-xs"
                  disabled={marcarAssinado.isPending}
                  onClick={() => marcarAssinado.mutate(asoAtivo.id)}>
                  {marcarAssinado.isPending ? "Marcando…" : "Marcar como assinado"}
                </button>
              ) : null}
              {asoAtivo.status !== "assinado" && !formCancelamento ? (
                <button className="btn-secondary px-2 py-1 text-xs text-anarosa"
                  onClick={() => setFormCancelamento(true)}>
                  Cancelar ASO
                </button>
              ) : null}
            </div>

            {asoAtivo.status !== "assinado" && !temAsoAssinadoAnexado ? (
              <p className="text-xs text-ink-muted">
                Para marcar como assinado, anexe antes a digitalização do ASO assinado
                (tipo “ASO assinado”) em <strong>Documentos digitalizados</strong>.
              </p>
            ) : null}
            {asoAtivo.status === "assinado" ? (
              <p className="text-xs text-ink-muted">
                Ciclo concluído: o papel assinado existe e está digitalizado. ASO assinado não
                pode ser cancelado.
              </p>
            ) : null}

            {formCancelamento ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3">
                <label className="mb-1 block text-xs font-medium text-red-900">
                  Motivo do cancelamento (mínimo 3 caracteres)
                </label>
                <textarea className="input min-h-20" value={motivoCancelamento}
                  onChange={(e) => setMotivoCancelamento(e.target.value)}
                  placeholder="Ex.: erro de digitação na função do colaborador — reemitir" />
                <p className="mt-1 text-xs text-red-900">
                  Cancelar destrava o atendimento pra correção. O ASO cancelado permanece no
                  histórico e a nova emissão recebe número novo.
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <button className="btn-primary px-2 py-1 text-xs"
                    disabled={motivoCancelamento.trim().length < 3 || cancelarAso.isPending}
                    onClick={() => handleCancelarAso(asoAtivo.id)}>
                    {cancelarAso.isPending ? "Cancelando…" : "Confirmar cancelamento"}
                  </button>
                  <button className="btn-secondary px-2 py-1 text-xs"
                    disabled={cancelarAso.isPending}
                    onClick={() => { setFormCancelamento(false); setMotivoCancelamento(""); }}>
                    Voltar
                  </button>
                </div>
              </div>
            ) : null}

            {marcarImpresso.error ? (
              <p className="text-sm text-anarosa">{(marcarImpresso.error as Error).message}</p>
            ) : null}
            {marcarAssinado.error ? (
              <p className="text-sm text-anarosa">{(marcarAssinado.error as Error).message}</p>
            ) : null}
            {cancelarAso.error ? (
              <p className="text-sm text-anarosa">{(cancelarAso.error as Error).message}</p>
            ) : null}
          </div>
        ) : atend.parecer ? (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button className="btn-primary" disabled={gerarAso.isPending}
              onClick={() => gerarAso.mutate()}>
              {gerarAso.isPending ? "Gerando…" : "Gerar ASO"}
            </button>
            <span className="text-xs text-ink-muted">
              Congela o snapshot, numera e gera o PDF (Modelo Padrão) — e trava a digitação.
            </span>
            {gerarAso.error ? (
              <p className="text-sm text-anarosa">{(gerarAso.error as Error).message}</p>
            ) : null}
          </div>
        ) : (
          <p className="mt-3 text-sm text-ink-muted">
            O ASO numerado só é emitido depois do parecer. Até lá, use a impressão para
            preenchimento manual.
          </p>
        )}

        {asoCancelado ? (
          <div className="mt-4 border-t border-gray-100 pt-3 text-sm">
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-ink-muted">
              Histórico
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-xs font-semibold line-through">{asoCancelado.numero}</span>
              <span className={STATUS_ASO_BADGE.cancelado.classe}>
                {STATUS_ASO_BADGE.cancelado.rotulo}
              </span>
              <span className="text-ink-muted">
                gerado em {fmtData(asoCancelado.gerado_em)}
                {asoCancelado.cancelado_em ? ` · cancelado em ${fmtDataHora(asoCancelado.cancelado_em)}` : ""}
                {asoCancelado.cancelado_por ? ` por ${asoCancelado.cancelado_por}` : ""}
              </span>
            </div>
            <p className="mt-1 text-ink-muted">
              <span className="font-medium">Motivo:</span>{" "}
              {motivoRegistrado ?? "registrado na auditoria do cancelamento."}
            </p>
          </div>
        ) : null}

        {erroPdf ? <p className="mt-2 text-sm text-anarosa">{erroPdf}</p> : null}
      </div>
    </div>
  );
}
